import React, { forwardRef, useEffect, useImperativeHandle } from "react";
import BaseCard from "../../../components/ui/BaseCard";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  updateWorkOrderForm,
  updateWorkOrderFormValidation,
} from "../../../store/workOrder/workOrderSlice";
import { RadioGroup } from "@mui/material";
import BaseRadio from "../../../components/ui/BaseRadio";
import HardStart from "./ScheduleFormTypes/HardStart";
import WorkBetween from "./ScheduleFormTypes/WorkBetween";
import WorkAnytime from "./ScheduleFormTypes/WorkAnytime";
import { showToast } from "../../../store/toast/toastSlice";
import useAuth from "../../../hooks/useAuth";
import { UserType } from "../../../utils/enum";

const TIME_SHORTCUTS = [];

const ScheduleForm = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const { authRole } = useAuth();
  const isCustomer = authRole === UserType.CUSTOMER;
  const schedule = useSelector((state) => state.workOrder.scheduling.schedule);
  const location = useSelector((state) => state.workOrder.scheduling.location);

  // The schedule is entered in the site's local time, and that timezone comes
  // from the location — LocationForm writes schedule.timeZone when one is
  // picked. Choosing a date before then would be interpreted against the wrong
  // zone, so the schedule stays locked until a location exists.
  // Matches LocationForm's own validity rules for each location type.
  const isLocationSelected =
    location?.type === "REMOTE"
      ? true
      : location?.type === "SAVED_LOCATION"
        ? !!location?.addressId
        : !!location?.latitude && !!location?.longitude;

  // `isEditMode` is set only by EditScheduleModal, which renders this form on its
  // own to edit one work order's schedule. It hydrates the zone but never touches
  // `location`, so the store there still holds whatever the wizard last left —
  // judging the gate on that could unlock a work order that has no location at
  // all. In that context the work order's own timezone is the only honest signal.
  //
  // In the wizard, LocationForm sits alongside and keeps `location` current, so
  // either signal is valid and the gate opens as soon as one is chosen.
  const canSchedule = props.isEditMode
    ? !!schedule?.timeZone
    : isLocationSelected || !!schedule?.timeZone;

  useImperativeHandle(ref, () => ({
    isValid: () => {
      const errors = [];

      const fail = (fieldName, message) => {
        errors.push(message);
        dispatch(updateWorkOrderFormValidation({ fieldName, data: true }));
      };

      if (!schedule?.orderStartDate || !dayjs(schedule.orderStartDate).isValid()) {
        fail("orderStartDate", "Start date is required");
      }
      if (!schedule?.orderStartTime || !dayjs(schedule.orderStartTime).isValid()) {
        fail("orderStartTime", "Start time is required");
      }

      return { valid: errors.length === 0, errors };
    },
  }));
  useEffect(() => {
    if (props.isEditMode && props.workOrderData) {
      const data = props.workOrderData;
      const hydratedSchedule = {
        scheduleType: data.scheduleType || "HARD_START",
        orderStartDate: data.orderStartDate || "",
        orderStartTime: data.orderStartTime || "",
        orderEndDate: data.orderEndDate || "",
        orderEndTime: data.orderEndTime || "",
        confirmedWorkOrderAt: data.confirmedWorkOrderAt || "",
        onTheWayWorkOrderAt: data.onTheWayWorkOrderAt || "",
        runningLateWorkOrderAt: data.runningLateWorkOrderAt || "",
        runningLateWorkOrderReason: data.runningLateWorkOrderReason || "",
        timeZone: data.timeZone || "",
      };

      dispatch(
        updateWorkOrderForm({
          step: "scheduling",
          data: { schedule: hydratedSchedule },
        }),
      );
      // Baseline for the edit modal's Save Changes button — see useSectionChanges.
      props.onHydrate?.(hydratedSchedule);
    }
  }, [props.isEditMode, props.workOrderData, dispatch]);

  const handleChange = (name, value) => {
    dispatch(
      updateWorkOrderForm({
        step: "scheduling",
        data: {
          schedule: {
            [name]: value,
          },
        },
      }),
    );
  };

  const handleSetTomorrow8AM = () => {
    const tomorrowAt8AM = dayjs().add(1, "day").hour(8).minute(0).second(0).millisecond(0);

    // Format the date and time
    const formattedDateTime = dayjs(tomorrowAt8AM).utcOffset(0, true).format("YYYY-MM-DDTHH:mm:ss");
    dispatch(
      updateWorkOrderForm({
        step: "scheduling",
        data: {
          schedule: {
            orderStartDate: tomorrowAt8AM.toISOString(),
            orderStartTime: formattedDateTime,
          },
        },
      }),
    );
  };
  const handleSetToday = (time) => {
    const today = dayjs().hour(time).minute(0).second(0);

    // Format the date and time
    const formattedDateTime = dayjs(today).utcOffset(0, true).format("YYYY-MM-DDTHH:mm:ss");

    dispatch(
      updateWorkOrderForm({
        step: "scheduling",
        data: {
          schedule: {
            orderStartDate: today.toISOString(),
            orderStartTime: formattedDateTime,
          },
        },
      }),
    );
  };

  return (
    <BaseCard className="py-4 lg:px-16 px-5 mt-2 rounded-lg" elevation={1}>
      <div className="border-bottom mb-4">
        <h6 className="mb-2 text-dark">Schedule</h6>
      </div>

      {/* The schedule is entered as wall-clock time at the site, so it cannot be
          filled in before a location has fixed the timezone. Rather than disable
          the inputs, the controls are simply not rendered — there is then nothing
          to click, tab into, or work around. */}
      {!canSchedule ? (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-3">
          <i className="fa-solid fa-location-dot mt-[3px] text-[13px] text-amber-500" />
          <div>
            <p className="text-sm font-medium text-amber-900">Select a location first</p>
            {/* Worded for both places this form appears: the create/edit wizard,
                where the location form sits directly above, and the Edit Schedule
                modal, where it does not. */}
            <p className="mt-0.5 text-xs text-amber-800">
              The date and time are set in the site's local time zone, which comes from the work
              order's location. Set the location and the schedule will appear here.
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="col-span-2">
            <RadioGroup
              aria-labelledby="demo-radio-buttons-group-label"
              defaultValue="HARD_START"
              name="radio-buttons-group"
              onChange={(e) => handleChange("scheduleType", e.target.value)}
            >
              <div>
                <BaseRadio label="A specific date & time (Fixed Start)" value="HARD_START" />

                {!isCustomer && (
                  <>
                    <BaseRadio label="Complete Work Between specific hours" value="WORK_BETWEEN" />

                    <BaseRadio
                      label="Complete Work anytime over a date range"
                      value="WORK_ANYTIME"
                    />
                  </>
                )}
              </div>
            </RadioGroup>
          </div>

          {schedule.scheduleType === "HARD_START" && <HardStart />}
        </>
      )}
      {/* {schedule.scheduleType === "WORK_BETWEEN" && <WorkBetween />} */}
      {/* {schedule.scheduleType === "WORK_ANYTIME" && <WorkAnytime />} */}

      {/* <p className="text-sm font-medium text-dark my-4">
        Quick options:
        <span
          className="text-secondary font-light cursor-pointer ml-2"
          onClick={handleSetTomorrow8AM}
        >
          Tomorrow 8am
        </span>
        <span
          className="text-secondary font-light cursor-pointer ml-2"
          onClick={() => handleSetToday(10)}
        >
          , 10am
        </span>
        <span
          className="text-secondary font-light cursor-pointer ml-2"
          onClick={() => handleSetToday(12)}
        >
          , 12pm
        </span>
        <span
          className="text-secondary font-light cursor-pointer ml-2"
          onClick={() => handleSetToday(17)}
        >
          , 5pm
        </span>
        <span
          className="text-secondary font-light cursor-pointer ml-2"
          onClick={() => handleSetToday(22)}
        >
          , 10pm
        </span>
        <span
          className="text-secondary font-light cursor-pointer ml-2"
          onClick={() => handleSetToday(24)}
        >
          , 12am
        </span>
      </p> */}
    </BaseCard>
  );
});

export default ScheduleForm;
